/* eslint-disable */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { FieldAttributes, useField } from "formik";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@material-ui/core";
import { SelectData } from "./selectData";
import { useScrollLockOnSelect } from "../../../hooks/useScrollLock";

/**
 * @interface CustomSelectProps
 */
interface CustomSelectProps {
  id: string;
  labelId: string;
  label: string;
  options: SelectData[];
  defaultText?: string; // used for empty option
  defaultValueForEmptyElement?: number | string;
  noEmptyDefaultValue?: boolean;
}

/**
 * CustomSelect
 *
 * A reusable Formik-connected Select component using Material-UI.
 * It supports internationalized option labels, validation error display,
 * and scroll-locking the page when the dropdown is open.
 *
 * Props:
 * @param {string} id - The id of the select element.
 * @param {string} labelId - The id of the label element.
 * @param {string} label - The label for the select element.
 * @param {SelectData[]} options - An array of options for the select element.
 * @param {string} defaultText - The text for the empty option.
 * @param {number | string} defaultValueForEmptyElement - The value for the empty option.
 * @param {boolean} noEmptyDefaultValue - If true, the empty option will not have a default value.
 * - All standard MUI Select and Formik field props are supported
 *
 * @returns {JSX.Element} The rendered CustomSelect component.
 */
const CustomSelect: React.FunctionComponent<
  FieldAttributes<{}> & CustomSelectProps & SelectProps
> = ({ labelId, label, options, required, ...props }) => {
  const { t } = useTranslation(["common", "enums", "snackbars"]);
  const [field, meta] = useField<{}>(props);
  const error = meta.error && meta.touched ? meta.error : "";
  const { handleOpen, handleClose } = useScrollLockOnSelect();

  return (
    <FormControl fullWidth variant="filled" error={!!error}>
      <InputLabel required={required} id={labelId}>
        {label}
      </InputLabel>
      <Select
        {...field}
        {...props}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={{
          disableAutoFocus: true,
          disableScrollLock: true,
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          MenuListProps: {
            onFocus: (e: React.FocusEvent) => e.preventDefault(),
          },
        }}
      >
        {options?.map((option) => (
          <MenuItem key={option.key} value={option.value} disabled={!!option.disabled}>
            {option.label ? option.label : t(option.key, { ns: "enums" })}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{error}</FormHelperText>
    </FormControl>
  );
};

export default CustomSelect;
